import { useMemo } from "react";
import type { CartTotalsDto } from "../api";
import type { CartItem } from "../state/cart";
import { money, monogram, Sheet, tintFor } from "../ui";

/**
 * Корзина, разложенная по заведениям (п. 9 ТЗ).
 *
 * Это главное отличие от обычной доставки, и оно должно быть видно до оплаты:
 * заказ поедет к гостю несколькими независимыми частями, из разных кухонь, и
 * каждая придёт своим ходом. По плоскому списку понять это нельзя.
 *
 * Суммы берутся из ответа сервера, а не складываются здесь: цены живут в базе,
 * и второй расчёт в браузере — это второй источник правды о деньгах.
 */
export function CartSheet({
  items,
  totals,
  pricing,
  problem,
  promoCode,
  promoError,
  onPromoCode,
  onClose,
  onQty,
  onCheckout
}: {
  items: CartItem[];
  totals: CartTotalsDto;
  pricing: boolean;
  problem: string | null;
  promoCode: string;
  promoError: string | null;
  onPromoCode: (code: string) => void;
  onClose: () => void;
  onQty: (index: number, qty: number) => void;
  onCheckout: () => void;
}) {
  /** Позиции сгруппированы по заведению с сохранением исходных индексов. */
  const groups = useMemo(() => {
    const map = new Map<string, { name: string; entries: { item: CartItem; index: number }[] }>();

    items.forEach((item, index) => {
      const group = map.get(item.pointId);
      if (group) group.entries.push({ item, index });
      else map.set(item.pointId, { name: item.pointName, entries: [{ item, index }] });
    });

    return [...map.entries()].map(([pointId, group]) => ({ pointId, ...group }));
  }, [items]);

  const serverTotal = totals.points.length > 0 ? totals.total : null;
  const discount = totals.partnerDiscount + totals.platformDiscount;

  return (
    <Sheet
      title="Ваш заказ"
      note={
        groups.length > 1
          ? `${groups.length} заведения — каждое принесёт свою часть`
          : undefined
      }
      onClose={onClose}
      footer={
        <>
          {discount > 0 ? (
            <div className="saved">
              <span>Скидка{totals.promo ? ` · ${totals.promo.code}` : ""}</span>
              <strong>−{money(discount)}</strong>
            </div>
          ) : null}

          <div className="total">
            <span className="muted">Итого</span>
            <strong>{pricing && serverTotal === null ? "…" : money(serverTotal ?? 0)}</strong>
          </div>

          {problem ? <p className="notice" style={{ marginBottom: 12 }}>{problem}</p> : null}

          <button
            type="button"
            className="btn"
            disabled={items.length === 0 || serverTotal === null || problem !== null}
            onClick={onCheckout}
          >
            Оформить
          </button>
        </>
      }
    >
      {items.length === 0 ? (
        <p className="empty">Пока пусто. Загляните в заведения парка.</p>
      ) : (
        <>
          {groups.map(group => {
            const serverGroup = totals.points.find(point => point.pointId === group.pointId);

            return (
              <div key={group.pointId} className="vendor-group">
                <div className="vendor-head">
                  <span className="chip" style={{ background: tintFor(group.name) }}>
                    {monogram(group.name)}
                  </span>
                  <strong>{group.name}</strong>
                  {serverGroup ? (
                    <span className="vendor-sum">{money(serverGroup.total)}</span>
                  ) : null}
                </div>

                {group.entries.map(({ item, index }, position) => {
                  /*
                   * Сумма позиции берётся из ответа сервера, а не считается
                   * здесь.
                   *
                   * Иначе с появлением акций строка и итог заведения
                   * расходятся: «Эспрессо 320 ₽» под заголовком «160 ₽».
                   * Гость видит противоречие и перестаёт верить обеим цифрам.
                   *
                   * Порядок строк внутри заведения сервер сохраняет, поэтому
                   * сопоставление идёт по позиции. Пока ответа нет, показываем
                   * цену без скидки — это честнее пустого места.
                   */
                  const priced = serverGroup?.lines[position];

                  return (
                  <div key={`${item.productId}-${index}`} className="line">
                    <div className="line-body">
                      <div className="line-title">{item.title}</div>
                      {item.modifiers.length > 0 ? (
                        <div className="line-mods">
                          {item.modifiers.map(modifier => modifier.title).join(", ")}
                        </div>
                      ) : null}
                    </div>

                    <div className="stepper">
                      <button
                        type="button"
                        onClick={() => onQty(index, item.qty - 1)}
                        aria-label="Убрать одну"
                      >
                        −
                      </button>
                      <span>{item.qty}</span>
                      <button
                        type="button"
                        onClick={() => onQty(index, item.qty + 1)}
                        aria-label="Добавить одну"
                      >
                        +
                      </button>
                    </div>

                    <div className="line-sum">
                      {priced && priced.discount > 0 ? (
                        <s className="line-was">{money(priced.baseTotal)}</s>
                      ) : null}
                      {money(priced ? priced.total : item.unitPrice * item.qty)}
                    </div>
                  </div>
                  );
                })}
              </div>
            );
          })}

          {/*
            Поле промокода стоит под составом, а не над ним: гость сначала
            собирает заказ и только потом вспоминает про код с листовки.
          */}
          <div className="promo">
            <input
              value={promoCode}
              onChange={event => onPromoCode(event.target.value.toUpperCase())}
              placeholder="Промокод"
              aria-label="Промокод"
              autoCapitalize="characters"
              autoComplete="off"
              maxLength={32}
            />
            {totals.promo ? <span className="promo-ok">применён</span> : null}
          </div>

          {promoError ? <p className="promo-error">{promoError}</p> : null}
        </>
      )}
    </Sheet>
  );
}
